/**
 * Settings API Hooks
 * React hooks for admin settings endpoints
 * Includes radius/wait time, email/SMS templates, and integration settings
 */

import { useFetch, buildUrl, useMutation } from './useApi';
import { ADMIN_API_BASE_URL } from '../config';
import { getAuthToken } from './useAdminRefactored';
import type { ApiResponse } from './types';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Radius and wait time settings response
 * Uses 3-level configuration with distance and wait time per level
 */
export interface RadiusWaitTimeSettings {
  id?: number;
  level1DistanceKm: number;
  level1WaitTimeMin: number;
  level2DistanceKm: number;
  level2WaitTimeMin: number;
  level3DistanceKm: number;
  level3WaitTimeMin: number;
  createdBy?: string;
  createdDate?: string;
  isDeletedValue?: boolean;
  isActive: boolean;
  modifiedBy?: string;
  modifiedDate?: string;
}

/**
 * Update radius and wait time settings request
 */
export interface UpdateRadiusWaitTimeRequest {
  id?: number;
  level1DistanceKm: number;
  level1WaitTimeMin: number;
  level2DistanceKm: number;
  level2WaitTimeMin: number;
  level3DistanceKm: number;
  level3WaitTimeMin: number;
  isActive?: boolean;
}

/**
 * Ride visibility settings response
 */
export interface RideVisibilitySettings {
  id?: number;
  level1DistanceKm: number;
  level1WaitTimeMin: number;
  level2DistanceKm: number;
  level2WaitTimeMin: number;
  level3DistanceKm: number;
  level3WaitTimeMin: number;
  pitStopAllowed?: boolean;
  maxAllowedPitstops?: number;
  createdBy?: string;
  createdDate?: string;
  isDeletedValue?: boolean;
  isActive: boolean;
  modifiedBy?: string;
  modifiedDate?: string;
  pitStopCharges?: number;
  platformFeePercent?: number;
  currency?: string;
}

/**
 * Create ride visibility settings request
 */
export interface CreateRideVisibilityRequest {
  level1DistanceKm: number;
  level1WaitTimeMin: number;
  level2DistanceKm: number;
  level2WaitTimeMin: number;
  level3DistanceKm: number;
  level3WaitTimeMin: number;
  pitStopAllowed?: boolean;
  maxAllowedPitstops?: number;
  isActive?: boolean;
}

/**
 * Update ride visibility settings request
 */
export interface UpdateRideVisibilityRequest {
  id: number;
  level1DistanceKm: number;
  level1WaitTimeMin: number;
  level2DistanceKm: number;
  level2WaitTimeMin: number;
  level3DistanceKm: number;
  level3WaitTimeMin: number;
  pitStopAllowed?: boolean;
  maxAllowedPitstops?: number;
  isActive?: boolean;
}

/**
 * Email template
 */
export interface EmailTemplate {
  id?: number;
  templateCode: string;
  templateName: string;
  subject: string;
  body: string;
  isHtml: boolean;
  variables: string[];
  isActive: boolean;
  createdBy?: string;
  createdDate?: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

/**
 * Update email template request
 */
export interface UpdateEmailTemplateRequest {
  templateCode: string;
  subject?: string;
  body?: string;
  isHtml?: boolean;
  isActive?: boolean;
}

/**
 * SMS template
 */
export interface SmsTemplate {
  id?: number;
  templateCode: string;
  templateName: string;
  message: string;
  variables: string[];
  isActive: boolean;
  createdBy?: string;
  createdDate?: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

/**
 * Update SMS template request
 */
export interface UpdateSmsTemplateRequest {
  templateCode: string;
  message?: string;
  isActive?: boolean;
}

/**
 * Integration provider type
 */
export type IntegrationProvider = 'STRIPE' | 'RAZORPAY' | 'GOOGLE_MAPS' | 'MAPBOX' | 'TWILIO' | 'AWS_SNS' | 'SENDGRID' | 'MAILGUN';

/**
 * Integration type
 */
export type IntegrationType = 'PAYMENT' | 'MAPS' | 'SMS_GATEWAY' | 'EMAIL_GATEWAY';

/**
 * Integration settings
 */
export interface IntegrationSettings {
  id?: number;
  integrationType: IntegrationType;
  provider: IntegrationProvider;
  displayName: string;
  isActive: boolean;
  isDefault: boolean;
  apiKey?: string;
  apiSecret?: string;
  apiEndpoint?: string;
  webhookUrl?: string;
  sandboxMode: boolean;
  additionalConfig: Record<string, string>;
  createdBy?: string;
  createdDate?: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

/**
 * Update integration settings request
 */
export interface UpdateIntegrationSettingsRequest {
  integrationType?: IntegrationType;
  provider?: IntegrationProvider;
  displayName?: string;
  isActive?: boolean;
  isDefault?: boolean;
  apiKey?: string;
  apiSecret?: string;
  apiEndpoint?: string;
  webhookUrl?: string;
  sandboxMode?: boolean;
  additionalConfig?: Record<string, string>;
}

/**
 * Test integration request
 */
export interface TestIntegrationRequest {
  integrationType: IntegrationType;
  provider: IntegrationProvider;
  testData?: Record<string, any>;
}

/**
 * Test integration response
 */
export interface TestIntegrationResponse {
  success: boolean;
  message: string;
  details?: Record<string, any>;
}

// ============================================================================
// Email Settings Types
// ============================================================================

/**
 * Email settings configuration
 */
export interface EmailSettings {
  id?: number;
  email: string;
  password: string;
  name: string;
  createdBy?: string;
  createdDate?: string;
  isDeletedValue?: boolean;
  modifiedBy?: string;
  modifiedDate?: string;
  modifiedSystemIp?: string;
}

/**
 * Create email settings request
 */
export interface CreateEmailSettingsRequest {
  email: string;
  password: string;
  name: string;
  createdBy?: string;
  createdDate?: string;
  isDeletedValue?: boolean;
  modifiedBy?: string;
  modifiedDate?: string;
  modifiedSystemIp?: string;
}

/**
 * Update email settings request
 */
export interface UpdateEmailSettingsRequest {
  id: number;
  email: string;
  password: string;
  name: string;
  createdBy?: string;
  createdDate?: string;
  isDeletedValue?: boolean;
  modifiedBy?: string;
  modifiedDate?: string;
  modifiedSystemIp?: string;
}

// Response type aliases
export type RadiusWaitTimeSettingsResponse = ApiResponse<RadiusWaitTimeSettings>;
export type RideVisibilitySettingsResponse = ApiResponse<RideVisibilitySettings>;
export type EmailTemplatesResponse = ApiResponse<EmailTemplate[]>;
export type EmailTemplateResponse = ApiResponse<EmailTemplate>;
export type SmsTemplatesResponse = ApiResponse<SmsTemplate[]>;
export type SmsTemplateResponse = ApiResponse<SmsTemplate>;
export type IntegrationSettingsResponse = ApiResponse<IntegrationSettings[]>;
export type IntegrationSettingResponse = ApiResponse<IntegrationSettings>;
export type EmailSettingsResponse = ApiResponse<EmailSettings>;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get authentication headers
 */
function getAuthHeaders(): Record<string, string> {
  const token = getAuthToken();
  // return token ? { Authorization: `Bearer ${token}` } : {};
  return {};
}

// ============================================================================
// Radius and Wait Time Settings Hooks
// ============================================================================

/**
 * Hook to fetch stepwise radius and wait time configuration
 * GET /settings/radius-wait-time
 */
export function useRadiusWaitTimeSettings(
  options: {
    enabled?: boolean;
    onSuccess?: (data: RadiusWaitTimeSettings) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(ADMIN_API_BASE_URL, '/settings/radius-wait-time');

  const response = useFetch<RadiusWaitTimeSettingsResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: (data) => options.onSuccess?.(data.data),
    onError: options.onError,
  });

  return {
    ...response,
    data: response.data?.data || null,
  };
}

/**
 * Hook to update radius and wait time settings
 * PUT /settings/radius-wait-time
 */
export function useUpdateRadiusWaitTimeSettings(
  options: {
    onSuccess?: (data: RadiusWaitTimeSettings) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<RadiusWaitTimeSettings, UpdateRadiusWaitTimeRequest>(
    async (variables) => {
      const url = buildUrl(ADMIN_API_BASE_URL, '/settings/radius-wait-time');

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(variables),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: RadiusWaitTimeSettingsResponse = await response.json();
      return result.data;
    },
    {
      onSuccess: (data, variables) => options.onSuccess?.(data),
      onError: (error, variables) => options.onError?.(error),
    }
  );
}

// ============================================================================
// Email Template Hooks
// ============================================================================

/**
 * Hook to fetch all email templates
 * GET /settings/email-templates
 */
export function useEmailTemplates(
  options: {
    enabled?: boolean;
    onSuccess?: (data: EmailTemplate[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(ADMIN_API_BASE_URL, '/settings/email-templates');

  const response = useFetch<EmailTemplatesResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: (data) => options.onSuccess?.(data.data || []),
    onError: options.onError,
  });

  return {
    ...response,
    data: response.data?.data || null,
  };
}

/**
 * Hook to update an email template
 * PUT /settings/email-templates/{templateCode}
 */
export function useUpdateEmailTemplate(
  options: {
    onSuccess?: (data: EmailTemplate) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<EmailTemplate, UpdateEmailTemplateRequest>(
    async (variables) => {
      const url = buildUrl(ADMIN_API_BASE_URL, `/settings/email-templates/${variables.templateCode}`);

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(variables),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: EmailTemplateResponse = await response.json();
      return result.data;
    },
    {
      onSuccess: (data, variables) => options.onSuccess?.(data),
      onError: (error, variables) => options.onError?.(error),
    }
  );
}

// ============================================================================
// SMS Template Hooks
// ============================================================================

/**
 * Hook to fetch all SMS templates
 * GET /settings/sms-templates
 */
export function useSmsTemplates(
  options: {
    enabled?: boolean;
    onSuccess?: (data: SmsTemplate[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(ADMIN_API_BASE_URL, '/settings/sms-templates');

  const response = useFetch<SmsTemplatesResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: (data) => options.onSuccess?.(data.data || []),
    onError: options.onError,
  });

  return {
    ...response,
    data: response.data?.data || null,
  };
}

/**
 * Hook to update an SMS template
 * PUT /settings/sms-templates/{templateCode}
 */
export function useUpdateSmsTemplate(
  options: {
    onSuccess?: (data: SmsTemplate) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<SmsTemplate, UpdateSmsTemplateRequest>(
    async (variables) => {
      const url = buildUrl(ADMIN_API_BASE_URL, `/settings/sms-templates/${variables.templateCode}`);

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(variables),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: SmsTemplateResponse = await response.json();
      return result.data;
    },
    {
      onSuccess: (data, variables) => options.onSuccess?.(data),
      onError: (error, variables) => options.onError?.(error),
    }
  );
}

// ============================================================================
// Integration Settings Hooks
// ============================================================================

/**
 * Hook to fetch integration settings (payment, maps, SMS gateway)
 * GET /settings/integrations
 */
export function useIntegrationSettings(
  integrationType?: IntegrationType,
  options: {
    enabled?: boolean;
    onSuccess?: (data: IntegrationSettings[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryParams: Record<string, any> = {};
  if (integrationType) {
    queryParams.type = integrationType;
  }

  const url = buildUrl(ADMIN_API_BASE_URL, '/settings/integrations', queryParams);

  const response = useFetch<IntegrationSettingsResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: (data) => options.onSuccess?.(data.data || []),
    onError: options.onError,
  });

  return {
    ...response,
    data: response.data?.data || null,
  };
}

/**
 * Hook to update integration settings
 * PUT /settings/integrations/{id}
 */
export function useUpdateIntegrationSettings(
  options: {
    onSuccess?: (data: IntegrationSettings) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<IntegrationSettings, { id: number } & UpdateIntegrationSettingsRequest>(
    async (variables) => {
      const { id, ...updateData } = variables;
      const url = buildUrl(ADMIN_API_BASE_URL, `/settings/integrations/${id}`);

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: IntegrationSettingResponse = await response.json();
      return result.data;
    },
    {
      onSuccess: (data, variables) => options.onSuccess?.(data),
      onError: (error, variables) => options.onError?.(error),
    }
  );
}

/**
 * Hook to test an integration connection
 * POST /settings/integrations/test
 */
export function useTestIntegration(
  options: {
    onSuccess?: (data: TestIntegrationResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<TestIntegrationResponse, TestIntegrationRequest>(
    async (variables) => {
      const url = buildUrl(ADMIN_API_BASE_URL, '/settings/integrations/test');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(variables),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: ApiResponse<TestIntegrationResponse> = await response.json();
      return result.data;
    },
    {
      onSuccess: (data, variables) => options.onSuccess?.(data),
      onError: (error, variables) => options.onError?.(error),
    }
  );
}

// ============================================================================
// Email Settings Hooks
// ============================================================================

/**
 * Hook to fetch email settings
 * GET /email-settings/get-email-settings
 */
export function useEmailSettings(
  options: {
    enabled?: boolean;
    onSuccess?: (data: EmailSettings) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(ADMIN_API_BASE_URL, '/email-settings/get-email-setting');

  const response = useFetch<EmailSettingsResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: (data) => options.onSuccess?.(data.data),
    onError: options.onError,
  });

  return {
    ...response,
    data: response.data?.data || null,
  };
}

/**
 * Hook to create email settings
 * POST /email-settings
 */
export function useCreateEmailSettings(
  options: {
    onSuccess?: (data: EmailSettings) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<EmailSettings, CreateEmailSettingsRequest>(
    async (variables) => {
      const url = buildUrl(ADMIN_API_BASE_URL, '/email-settings');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(variables),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: EmailSettingsResponse = await response.json();
      return result.message;
    },
    {
      onSuccess: (data, variables) => options.onSuccess?.(data),
      onError: (error, variables) => options.onError?.(error),
    }
  );
}

/**
 * Hook to update email settings
 * PUT /email-settings/{id}
 */
export function useUpdateEmailSettings(
  options: {
    onSuccess?: (data: EmailSettings) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<EmailSettings, UpdateEmailSettingsRequest>(
    async (variables) => {
      const url = buildUrl(ADMIN_API_BASE_URL, `/email-settings/${variables.id}`);

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(variables),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: EmailSettingsResponse = await response.json();
      return result.data;
    },
    {
      onSuccess: (data, variables) => options.onSuccess?.(data),
      onError: (error, variables) => options.onError?.(error),
    }
  );
}

/**
 * Diagnose mail request
 */
export interface DiagnoseMailRequest {
  toEmail: string;
}

/**
 * Diagnose mail response
 */
export interface DiagnoseMailResponse {
  success: boolean;
  message: string;
}

/**
 * Hook to diagnose/test email
 * GET /email-settings/diagnose-mail?testEmail=...
 */
export function useDiagnoseMail(
  options: {
    onSuccess?: (data: DiagnoseMailResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<DiagnoseMailResponse, DiagnoseMailRequest>(
    async (variables) => {
      const url = buildUrl(ADMIN_API_BASE_URL, '/email-settings/diagnose-mail', {
        testEmail: variables.toEmail,
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      // API may return plain boolean (true) or wrapped { data: ... }
      if (typeof result === 'boolean') {
        return { success: result, message: result ? 'Test email sent successfully' : 'Failed to send test email' } as DiagnoseMailResponse;
      }

      if (result && result.data) {
        return result.data;
      }

      return { success: true, message: result?.message || 'Test email sent successfully' } as DiagnoseMailResponse;
    },
    {
      onSuccess: (data, variables) => options.onSuccess?.(data),
      onError: (error, variables) => options.onError?.(error),
    }
  );
}

// ============================================================================
// Ride Visibility Settings Hooks
// ============================================================================

/**
 * Hook to fetch ride visibility settings
 * GET /ride-visibility-settings/get-ride-visibility-setting
 */
export function useRideVisibilitySettings(
  options: {
    enabled?: boolean;
    onSuccess?: (data: RideVisibilitySettings) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(ADMIN_API_BASE_URL, '/ride-visibility-settings/get-ride-visibility-setting');

  const response = useFetch<RideVisibilitySettingsResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: (data) => options.onSuccess?.(data.data),
    onError: options.onError,
  });

  return {
    ...response,
    data: response.data?.data || null,
  };
}

/**
 * Hook to create ride visibility settings
 * POST /ride-visibility-settings
 */
export function useCreateRideVisibilitySettings(
  options: {
    onSuccess?: (data: RideVisibilitySettings) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<RideVisibilitySettings, CreateRideVisibilityRequest>(
    async (variables) => {
      const url = buildUrl(ADMIN_API_BASE_URL, '/ride-visibility-settings');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(variables),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: RideVisibilitySettingsResponse = await response.json();
      return result.data;
    },
    {
      onSuccess: (data, variables) => options.onSuccess?.(data),
      onError: (error, variables) => options.onError?.(error),
    }
  );
}

/**
 * Hook to update ride visibility settings
 * PUT /ride-visibility-settings/{id}
 */
export function useUpdateRideVisibilitySettings(
  options: {
    onSuccess?: (data: RideVisibilitySettings) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<RideVisibilitySettings, UpdateRideVisibilityRequest>(
    async (variables) => {
      const { id, ...updateData } = variables;
      const url = buildUrl(ADMIN_API_BASE_URL, `/ride-visibility-settings/${id}`);

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: RideVisibilitySettingsResponse = await response.json();
      return result.data;
    },
    {
      onSuccess: (data, variables) => options.onSuccess?.(data),
      onError: (error, variables) => options.onError?.(error),
    }
  );
}

export const getStoredCurrency = (): string => {
  if (typeof window === "undefined") return "";
  const stored = sessionStorage.getItem("currency");
  return stored || "SOS";
};

export const formatGlobalCurrency = (amount: number): string => {
  const currency = getStoredCurrency();

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};
